import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Plus,
    Edit3,
    Trash2,
    X,
    Loader2,
    LogOut,
    Briefcase,
    Mail,
    BookOpen,
    Users,
    Check,
    Eye,
    EyeOff,
} from "lucide-react";
import { adminAxios, clearToken } from "./auth";

const EMPTY_JOB = {
    title: "",
    type: "CDI",
    location: "Abidjan, Côte d'Ivoire",
    department: "",
    description: "",
    profile: "",
    deadline: "",
    apply_email: "",
    active: true,
};

const JOB_TYPES = ["CDI", "CDD", "Stage", "Alternance", "Freelance"];

function JobForm({ initial, onCancel, onSaved }) {
    const [form, setForm] = useState(initial || EMPTY_JOB);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const handle = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErr("");
        try {
            const payload = {
                ...form,
                apply_email: form.apply_email || null,
            };
            if (initial?.id) {
                await adminAxios.put(`/admin/jobs/${initial.id}`, payload);
            } else {
                await adminAxios.post(`/admin/jobs`, payload);
            }
            onSaved();
        } catch (e2) {
            setErr(
                e2.response?.data?.detail
                    ? typeof e2.response.data.detail === "string"
                        ? e2.response.data.detail
                        : "Données invalides"
                    : "Erreur d'enregistrement"
            );
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto">
            <form
                onSubmit={submit}
                data-testid="admin-job-form"
                className="bg-white border-2 border-ink w-full max-w-3xl p-6 md:p-8 my-8"
            >
                <div className="flex items-center justify-between mb-6">
                    <h3 className="font-display font-bold uppercase text-xl md:text-2xl text-ink">
                        {initial?.id ? "Modifier l'offre" : "Nouvelle offre"}
                    </h3>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="w-9 h-9 flex items-center justify-center hover:bg-paper-alt"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <label className="label">Intitulé du poste *</label>
                        <input
                            data-testid="job-title"
                            name="title"
                            value={form.title}
                            onChange={handle}
                            className="field"
                            required
                        />
                    </div>
                    <div>
                        <label className="label">Type de contrat *</label>
                        <select
                            data-testid="job-type"
                            name="type"
                            value={form.type}
                            onChange={handle}
                            className="field"
                        >
                            {JOB_TYPES.map((t) => (
                                <option key={t} value={t}>
                                    {t}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="label">Localisation *</label>
                        <input
                            data-testid="job-location"
                            name="location"
                            value={form.location}
                            onChange={handle}
                            className="field"
                            required
                        />
                    </div>
                    <div>
                        <label className="label">Département</label>
                        <input
                            data-testid="job-department"
                            name="department"
                            value={form.department}
                            onChange={handle}
                            className="field"
                        />
                    </div>
                    <div>
                        <label className="label">Date limite (libre)</label>
                        <input
                            data-testid="job-deadline"
                            name="deadline"
                            value={form.deadline}
                            onChange={handle}
                            className="field"
                            placeholder="ex: 31/12/2026 ou 2026-12-31"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="label">
                            Email réception candidatures (optionnel)
                        </label>
                        <input
                            data-testid="job-apply-email"
                            name="apply_email"
                            type="email"
                            value={form.apply_email || ""}
                            onChange={handle}
                            className="field"
                            placeholder="info@sitmec.com par défaut"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="label">Description *</label>
                        <textarea
                            data-testid="job-description"
                            name="description"
                            rows={5}
                            value={form.description}
                            onChange={handle}
                            className="field resize-none"
                            required
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="label">Profil recherché</label>
                        <textarea
                            data-testid="job-profile"
                            name="profile"
                            rows={4}
                            value={form.profile}
                            onChange={handle}
                            className="field resize-none"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="inline-flex items-center gap-3 cursor-pointer">
                            <input
                                data-testid="job-active"
                                type="checkbox"
                                name="active"
                                checked={form.active}
                                onChange={handle}
                                className="w-4 h-4 accent-mint"
                            />
                            <span className="text-sm text-ink">
                                Offre active (visible sur le site)
                            </span>
                        </label>
                    </div>
                </div>

                {err && (
                    <div className="mt-4 px-4 py-3 border border-destructive text-destructive text-sm">
                        {err}
                    </div>
                )}

                <div className="mt-6 flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-5 py-3 font-display text-xs uppercase tracking-widest text-ink hover:text-ink-muted"
                    >
                        Annuler
                    </button>
                    <button
                        type="submit"
                        data-testid="job-submit"
                        disabled={saving}
                        className="btn-primary disabled:opacity-50"
                    >
                        {saving ? (
                            <Loader2 className="animate-spin" size={16} />
                        ) : initial?.id ? (
                            "Enregistrer"
                        ) : (
                            "Créer l'offre"
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}

function JobsTab() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        const res = await adminAxios.get("/admin/jobs");
        setJobs(res.data.items || []);
        setLoading(false);
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const toggleActive = async (job) => {
        await adminAxios.put(`/admin/jobs/${job.id}`, { ...job, active: !job.active });
        refresh();
    };

    const remove = async (job) => {
        await adminAxios.delete(`/admin/jobs/${job.id}`);
        setConfirmDelete(null);
        refresh();
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-display font-bold uppercase text-2xl text-ink">
                    Offres d'emploi
                </h2>
                <button
                    data-testid="new-job-btn"
                    onClick={() => setEditing(EMPTY_JOB)}
                    className="btn-primary"
                >
                    <Plus size={16} /> Nouvelle offre
                </button>
            </div>

            {loading ? (
                <div className="py-12 text-center">
                    <Loader2 className="inline animate-spin" />
                </div>
            ) : jobs.length === 0 ? (
                <div className="border border-border bg-paper-alt p-10 text-center text-ink-muted">
                    Aucune offre pour l'instant. Cliquez sur « Nouvelle offre » pour
                    commencer.
                </div>
            ) : (
                <div className="space-y-3">
                    {jobs.map((job) => (
                        <div
                            key={job.id}
                            data-testid={`admin-job-${job.id}`}
                            className="bg-white border border-border hover:border-ink transition-colors p-5 flex flex-col md:flex-row md:items-center gap-4"
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <span
                                        className={`font-display text-xs uppercase tracking-widest px-2 py-0.5 ${
                                            job.active
                                                ? "bg-mint text-ink"
                                                : "bg-paper-alt text-ink-muted border border-border"
                                        }`}
                                    >
                                        {job.active ? "Active" : "Inactive"}
                                    </span>
                                    <span className="font-display text-xs uppercase tracking-widest text-ink-muted">
                                        {job.type}
                                    </span>
                                </div>
                                <div className="font-display font-bold text-lg text-ink leading-tight truncate">
                                    {job.title}
                                </div>
                                <div className="text-sm text-ink-muted mt-1">
                                    {job.location}
                                    {job.department ? ` · ${job.department}` : ""}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                    onClick={() => toggleActive(job)}
                                    className="px-3 py-2 border border-ink hover:bg-ink hover:text-white transition-colors text-sm flex items-center gap-2"
                                    title={job.active ? "Désactiver" : "Activer"}
                                >
                                    {job.active ? (
                                        <EyeOff size={14} />
                                    ) : (
                                        <Eye size={14} />
                                    )}
                                </button>
                                <button
                                    onClick={() => setEditing(job)}
                                    data-testid={`edit-job-${job.id}`}
                                    className="px-3 py-2 border border-ink hover:bg-ink hover:text-white transition-colors text-sm flex items-center gap-2"
                                >
                                    <Edit3 size={14} />
                                </button>
                                <button
                                    onClick={() => setConfirmDelete(job)}
                                    data-testid={`delete-job-${job.id}`}
                                    className="px-3 py-2 border border-destructive text-destructive hover:bg-destructive hover:text-white transition-colors text-sm flex items-center gap-2"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <JobForm
                    initial={editing}
                    onCancel={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        refresh();
                    }}
                />
            )}

            {confirmDelete && (
                <div className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white border-2 border-destructive p-6 max-w-md w-full">
                        <h3 className="font-display font-bold uppercase text-lg text-ink">
                            Supprimer cette offre ?
                        </h3>
                        <p className="mt-3 text-sm text-ink-soft">
                            « {confirmDelete.title} » — cette action est irréversible.
                        </p>
                        <div className="mt-6 flex items-center justify-end gap-3">
                            <button
                                onClick={() => setConfirmDelete(null)}
                                className="px-5 py-3 font-display text-xs uppercase tracking-widest text-ink hover:text-ink-muted"
                            >
                                Annuler
                            </button>
                            <button
                                data-testid="confirm-delete-btn"
                                onClick={() => remove(confirmDelete)}
                                className="bg-destructive text-white hover:bg-red-700 transition-colors px-6 py-3 font-display uppercase tracking-widest text-xs"
                            >
                                Supprimer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function GenericListTab({ endpoint, title, columns }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        adminAxios.get(endpoint).then((r) => {
            setItems(r.data.items || []);
            setLoading(false);
        });
    }, [endpoint]);
    return (
        <div>
            <h2 className="font-display font-bold uppercase text-2xl text-ink mb-6">
                {title} ({items.length})
            </h2>
            {loading ? (
                <Loader2 className="animate-spin" />
            ) : items.length === 0 ? (
                <div className="border border-border bg-paper-alt p-10 text-center text-ink-muted">
                    Aucune entrée.
                </div>
            ) : (
                <div className="overflow-x-auto border border-border bg-white">
                    <table className="w-full text-sm">
                        <thead className="bg-paper-alt border-b border-border">
                            <tr>
                                {columns.map((c) => (
                                    <th
                                        key={c.key}
                                        className="text-left font-display text-xs uppercase tracking-widest text-ink px-4 py-3"
                                    >
                                        {c.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((row, i) => (
                                <tr
                                    key={row.id || i}
                                    className="border-b border-border hover:bg-paper-alt/50"
                                >
                                    {columns.map((c) => (
                                        <td
                                            key={c.key}
                                            className="px-4 py-3 text-ink align-top"
                                        >
                                            {c.render
                                                ? c.render(row[c.key], row)
                                                : row[c.key] || "—"}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default function AdminDashboard() {
    const nav = useNavigate();
    const [tab, setTab] = useState("jobs");
    const [authChecked, setAuthChecked] = useState(false);

    useEffect(() => {
        adminAxios
            .get("/admin/verify")
            .then(() => setAuthChecked(true))
            .catch(() => {
                clearToken();
                nav("/admin");
            });
    }, [nav]);

    const logout = () => {
        clearToken();
        nav("/admin");
    };

    if (!authChecked) {
        return (
            <div className="min-h-screen bg-paper-alt flex items-center justify-center">
                <Loader2 className="animate-spin text-ink" />
            </div>
        );
    }

    const TABS = [
        { id: "jobs", label: "Offres", icon: Briefcase },
        { id: "applications", label: "Candidatures", icon: Users },
        { id: "contacts", label: "Contacts", icon: Mail },
        { id: "catalog", label: "Catalogues", icon: BookOpen },
    ];

    const formatDate = (d) => (d ? new Date(d).toLocaleString() : "—");
    const checkCell = (v) =>
        v ? <Check size={14} className="text-mint-hover" /> : "—";

    return (
        <div className="min-h-screen bg-paper-alt">
            {/* Top bar */}
            <header className="bg-ink text-white border-b border-mint">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img
                            src="/sitmec-logo.jpg"
                            alt="SITMEC"
                            className="h-10 w-auto object-contain"
                        />
                        <span className="font-display text-xs uppercase tracking-[0.3em] text-mint">
                            Admin
                        </span>
                    </div>
                    <button
                        onClick={logout}
                        data-testid="admin-logout"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-mint text-mint hover:bg-mint hover:text-ink transition-colors font-display text-xs uppercase tracking-widest"
                    >
                        <LogOut size={14} /> Déconnexion
                    </button>
                </div>
            </header>

            {/* Tabs */}
            <div className="bg-white border-b border-border">
                <div className="max-w-7xl mx-auto px-6 flex overflow-x-auto">
                    {TABS.map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            data-testid={`tab-${id}`}
                            onClick={() => setTab(id)}
                            className={`px-5 py-4 font-display text-xs uppercase tracking-widest border-b-2 flex items-center gap-2 transition-colors whitespace-nowrap ${
                                tab === id
                                    ? "border-mint text-ink"
                                    : "border-transparent text-ink-muted hover:text-ink"
                            }`}
                        >
                            <Icon size={14} /> {label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content */}
            <main className="max-w-7xl mx-auto px-6 py-10">
                {tab === "jobs" && <JobsTab />}
                {tab === "applications" && (
                    <GenericListTab
                        endpoint="/admin/applications"
                        title="Candidatures reçues"
                        columns={[
                            { key: "created_at", label: "Date", render: formatDate },
                            { key: "job_title", label: "Poste" },
                            { key: "name", label: "Candidat" },
                            { key: "email", label: "Email" },
                            { key: "phone", label: "Téléphone" },
                            {
                                key: "cv_link",
                                label: "CV",
                                render: (v) =>
                                    v ? (
                                        <a
                                            href={v}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-mint-hover underline"
                                        >
                                            Voir
                                        </a>
                                    ) : (
                                        "—"
                                    ),
                            },
                            { key: "email_sent", label: "Email envoyé", render: checkCell },
                        ]}
                    />
                )}
                {tab === "contacts" && (
                    <GenericListTab
                        endpoint="/admin/contacts"
                        title="Demandes de contact"
                        columns={[
                            { key: "created_at", label: "Date", render: formatDate },
                            { key: "name", label: "Nom" },
                            { key: "company", label: "Société" },
                            { key: "email", label: "Email" },
                            { key: "phone", label: "Téléphone" },
                            { key: "service", label: "Service" },
                            { key: "language", label: "Langue" },
                            { key: "email_sent", label: "Email envoyé", render: checkCell },
                        ]}
                    />
                )}
                {tab === "catalog" && (
                    <GenericListTab
                        endpoint="/admin/catalog-leads"
                        title="Demandes de catalogue"
                        columns={[
                            { key: "created_at", label: "Date", render: formatDate },
                            { key: "name", label: "Nom" },
                            { key: "company", label: "Société" },
                            { key: "email", label: "Email" },
                            { key: "phone", label: "Téléphone" },
                            { key: "language", label: "Version" },
                            { key: "fulfilled", label: "Envoyé", render: checkCell },
                        ]}
                    />
                )}
            </main>
        </div>
    );
}
